import {AbstractContainer} from "/modules/common/timeseries/AbstractContainer";
import * as $ from "jquery";
import Plotly from 'plotly.js-dist-min';
import '/modules/extended_report/css/timeseries/settings-selector.css';
import '/modules/extended_report/css/timeseries/settings-selector.responsive.css';
import {OSM} from "ol/source";
import {MERCATOR_CODE, WGS84_CODE} from "../../common/config/config";
import {transform} from "ol/proj";
import Map from 'ol/Map';
import {Feature, View} from "ol";
import TileLayer from "ol/layer/Tile";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import {Draw} from "ol/interaction";
import {Fill, Stroke, Style} from "ol/style";
import CircleStyle from "ol/style/Circle";
import {Point} from "ol/geom";
import {getDistance} from "ol/sphere";
import {Loader} from "../../common/ui/Loader";
import {arrayLast, formatNumber, nnTrim, prefixUrl} from "../../common/util";
import {dateFromStr, formatDate} from "../../common/time/TimeCalculator";
import {Dropdown, DropdownOption} from "../../common/ui/Dropdown";
import {CheckboxesSet} from "../../common/ui/CheckboxesSet";
import {MultiOptionList} from "../../common/ui/MultiOptionList";
import {reducePrecisionWgs84} from "../../common/geolib";


const URL_WORLDBANK_STATS_API = '//api.worldbank.org/v2/country/{0}/indicator/{1}?date={2}:{3}&format=json';

/**
 * A container that has elements to choose time and location settings
 * @extends AbstractContainer
 */
class SettingsSelector extends AbstractContainer {

    WB_STAT_CODES = [
        "SP.POP.TOTL",
        "EN.POP.DNST",
        "NY.GDP.PCAP.CD",
        "SP.DYN.LE00.IN",
        "SP.POP.1564.TO.ZS",
        "SE.ADT.LITR.ZS",
        "SI.POV.NAHC",
        "SM.POP.REFG",
        "SP.RUR.TOTL",
        "EG.USE.PCAP.KG.OE",
    ];

    MAIN_COUNTRY_COLOR = "rgba(231,165,37,.5)";

    COUNTRY_COLORS = [
        "#009d9a",
        "#6929c4",
        "#005d5d",
        "#9f1853",
        "#fa4d56",
        "#570408",
        "#198038",
        "#002d9c",
        "#1192e8",
        "#ee538b",
        "#b28600",
        "#012749",
        "#8a3800",
        "#a56eff"
    ];

    DEFAULT_INDEX = 'gdp_capita';

    /**
     *
     * @param id {string} An HTML id for this selector container
     * @param translator {Translator} A translator
     * @param townAroundPointUrl {string}
     * @param countryStatsUrl {string}
     * @param basicStatsUrl {string}
     * @param mapImagesUrl {string}
     * @param initialCoords {Array<number>} 2 position array with initialCoords lon lat in wgs84
     * @param initialDate {Date} Initially selected date
     * @param minDate {Date} Min date to show available dates to the user
     * @param maxDate {Date} Max date to show available dates to the user
     * @param timeCalculator {TimeCalculator} The time calculator of the main indicator of the report
     * @param coordsMapModal {Modal} A modal window to show a map to pick coordinates
     * @param runtimeConfig {RuntimeConfig} The map runtime config
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     */
    constructor(id, translator, townAroundPointUrl, countryStatsUrl, basicStatsUrl, mapImagesUrl, initialCoords, initialDate, minDate, maxDate, timeCalculator, coordsMapModal, runtimeConfig, isMobileDevice) {
        super(id, translator, isMobileDevice, runtimeConfig);
        this.timeCalculator = timeCalculator;
        this.refDate = initialDate;
        this.minDate = minDate;
        this.maxDate = maxDate;
        this.townAroundPointUrl = townAroundPointUrl;
        this.countryStatsUrl = countryStatsUrl;
        this.basicStatsUrl = basicStatsUrl;
        this.mapImagesUrl = mapImagesUrl;
        this.coordsMapModal = coordsMapModal;
        this.onChangeCallbacks = new Set();
        this.coords = initialCoords;
    }

    getClassName() {
        return "SettingSelector";
    }

    /**
     * Add a callback for when the location and/or time changes
     * @param callback {function}
     */
    onChange(callback) {
        if(callback)
            this.onChangeCallbacks.add(callback);
        return this;
    }

    isExpanded() {
        return this.getSessionProp("expanded") === true;
    }

    getContainerElement() {
        if (!this.containerElement) {
            const expanded = this.isExpanded();
            this.containerElement = $(
                '<div class="location-selector container ' + (expanded ? 'expanded' : '') + '">' +
                '   <div class="header" role="heading">' +
                '       <div class="title">' +
                '           <h3></h3>' +
                '       </div>' +
                '       <div class="buttons-container">' +
                '           <button class="expander ' + (expanded ? 'collapse' : 'expand') + '" type="button" title="' + this.translator.translate('tool.charts.expander') + '"></button>' +
                '       </div>' +
                '   </div>' +
                '   <div class="body" aria-expanded="' + expanded + '">' +
                '      <div class="column">' +
                '           <div class="date-picker simplified-date-picker">' +
                '               <div>' +
                '                   <div class="time-container until-container">' +
                '                       <label for="report_location_datepicker_dropdown_select" class="until">' + this.translator.translate('tool.charts.location.date') + '</label>' +
                '                       <div class="input-container"></div>' +
                '                   </div>' +
                '               </div>' +
                '           </div>' +
                '           <div class="lon coordinates"><label>' + this.translator.translate('tool.charts.location.lon') + '<br/>' +
                '               <input type="text"/></label>' +
                '           </div>' +
                '           <div class="lat coordinates"><label>' + this.translator.translate('tool.charts.location.lat') + '<br/>' +
                '               <input type="text"/></label>' +
                '           </div>' +
                '           <div class="ok coordinates"><button type="button" class="action">ok</button></div>' +
                '           <div class="map-link coordinates"><a>[' + this.translator.translate('tool.charts.location.openMap') + ']</a></div>' +
                '           <div class="desc"></div>' +
                '           <div class="stats"></div>' +
                '       </div>' +
                '       <div class="column">' +
                '           <div class="map-img"></div>' +
                '       </div>' +
                '       <div class="country-comparison-container">' +
                '           <div class="country-comparison-controller-container"><h3>' + this.translator.translate("tool.charts.location.comparison.title") + '</h3></div>' +
                '           <div class="country-comparison"></div>' +
                '       </div>' +
                '   </div>' +
                '</div>'
            );
            this.loader = new Loader("report_coords_loader", this.isMobileDevice, $('.body', this.containerElement));

            this._updateDateAndCoords(this.refDate, this.coords);

            $('.coordinates.ok button', this.containerElement).click(() => {
                this._updateDateAndCoords(this.refDate, [
                    Number($('.coordinates.lon input', this.containerElement).val()),
                    Number($('.coordinates.lat input', this.containerElement).val())
                ]);
                this.logStat("SET_COORDINATES_MANUALLY");
            });
            $('.coordinates input', this.containerElement).keyup(e => {
                if (e.key === "Enter") {
                    $('.coordinates.ok button', this.containerElement).click();
                }
            });

            $('.coordinates.map-link a', this.containerElement).click(() => {
                this._startCoordsMap();
                this.logStat("SHOW_COORDINATES_MAP");
            });

            const checkDateButtons = (date) => {
                const previousButton = $('button.previous-button.until', this.containerElement);
                const nextButton = $('button.next-button.until', this.containerElement);
                const nextDate = this.timeCalculator.getNextDate(date);
                const previousDate = this.timeCalculator.getPreviousDate(date);
                if (previousDate < this.minFrom)
                    previousButton.attr('disabled', 'disabled').addClass('disabled');
                else previousButton.removeAttr('disabled').removeClass('disabled');
                if (nextDate >= this.timeCalculator.fixDate(this.maxDate))
                    nextButton.attr('disabled', 'disabled').addClass('disabled');
                else nextButton.removeAttr('disabled').removeClass('disabled');
            };

            const setDropDownValue = (previous, dropdown) => {
                const date = dateFromStr(dropdown.getValue());
                let newDate = previous
                    ? this.timeCalculator.fixDate(this.timeCalculator.getPreviousDate(date))
                    : this.timeCalculator.fixDate(this.timeCalculator.getNextDate(date));
                dropdown.setValue(formatDate(newDate));
            }

            const generateDateInputUI = () => {
                const previousButton = $('<button title="' + this.translator.translate('tool.charts.time.previous') + '" class="previous-button until"></button>');
                const nextButton = $('<button title="' + this.translator.translate('tool.charts.time.next') + '" class="next-button until"></button>');

                let currDate = this.timeCalculator.fixDate(this.timeCalculator.getPreviousDate(this.maxDate));
                const options = [new DropdownOption(this.translator, this.isMobileDevice, '#' + this.timeCalculator.timeCodeToString(formatDate(currDate)), formatDate(currDate), undefined)];
                do {
                    currDate = this.timeCalculator.fixDate(this.timeCalculator.getPreviousDate(currDate));
                    options.push(new DropdownOption(this.translator, this.isMobileDevice, '#' + this.timeCalculator.timeCodeToString(formatDate(currDate)), formatDate(currDate), undefined));
                } while (currDate > this.minDate);
                options.reverse();
                const onChangeDropdown = (dropDown) => {
                    const date = dateFromStr(dropDown.getValue());
                    checkDateButtons(date);
                    this._updateDateAndCoords(date, this.coords);
                };
                const dropdown = new Dropdown(
                    this.translator,
                    this.isMobileDevice,
                    "report_location_datepicker_dropdown",
                    options,
                    formatDate(this.refDate),
                    true,
                    'tool.charts.location.date',
                    onChangeDropdown, undefined);
                $('.input-container', this.containerElement).append(previousButton).append(dropdown.getUIElement()).append(nextButton);
                checkDateButtons(this.refDate);
                previousButton.click(() => {
                    setDropDownValue(true, dropdown);
                    this.logStat('PREVIOUS_REFERENCE_PERIOD {' + dropdown.getValue() + '}');
                });
                nextButton.click(() => {
                    setDropDownValue(false, dropdown);
                    this.logStat('NEXT_REFERENCE_PERIOD {' + dropdown.getValue() + '}');
                });
            };
            generateDateInputUI();

            const comparisonButton = $('<a class="country-comparison-controller">[' + this.translator.translate("tool.charts.location.comparison.show") + ']</a>').click(() => {
                if (!this.countryComparisonLoaded) {
                    this._generateComparisonChart(
                        this.countryComparisonValues.countryCode,
                        this.countryComparisonValues.countryName,
                        this.countryComparisonValues.neighbors,
                        this.countryComparisonValues.otherCountries,
                        this.countryComparisonValues.stats,
                        this.countryComparisonValues.statsDefs
                    );
                }
                $('.country-comparison', this.uiElement).toggle();
                if ($('.country-comparison', this.uiElement).is(':visible'))
                    this.logStat("SHOW_COUNTRY_COMPARISON_CHART");
                else this.logStat("HIDE_COUNTRY_COMPARISON_CHART");
            });
            $('.body .country-comparison-controller-container', this.containerElement).append(comparisonButton);
        }
        return this.containerElement;
    }

    _generateComparisonChart(countryCode, countryName, neighbors, otherCountries, stats, statsDefs) {
        this.countryComparisonLoaded = true;
        const target = $('.country-comparison', this.uiElement).empty();

        const updateComparisonChart = () => {
            const countryContainer = $('.selected-country-container', target).empty();
            let i = 0;
            const countries = [{
                code: countryCode,
                name: countryName,
                color: this.MAIN_COUNTRY_COLOR
            }].concat(this.neighborsCheckboxesSet.getValue().map(c => ({
                code: c,
                name: neighbors[c],
                color: this.COUNTRY_COLORS[i++ % this.COUNTRY_COLORS.length]
            }))).concat(this.otherCountriesCheckboxesSet.getValue().map(c => ({
                code: c,
                name: otherCountries[c],
                color: this.COUNTRY_COLORS[i++ % this.COUNTRY_COLORS.length]
            })));
            countries.forEach(country => {
                countryContainer.append($('<div class="country"><div class="color" style="background-color: ' + country.color + '"></div>' + country.name + '</div>').data('code', country.code))
            });
            $($('div.country', countryContainer)[0]).addClass('selected');

            const renderChart = (chartData, defs) => {
                const min = Math.min.apply(Math, chartData.map(cd => cd.x[0]));
                const max = Math.max.apply(Math, chartData.map(cd => arrayLast(cd.x)));
                chartData.forEach(cd => {
                    const x = cd.x, y = cd.y;
                    let i = 0;
                    for (let year = min; year <= max; year++) {
                        if (x[i] > year) {
                            x.splice(i, 0, year);
                            y.splice(i, 0, null);
                        }
                        i++;
                    }
                    cd.x = x;
                    cd.y = y;
                });

                const config = {
                    responsive: true,
                    showSendToCloud: false,
                    modeBarButtonsToRemove: ['resetscale', 'select2d', 'lasso2d', 'zoom2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'pan2d'],
                    modebarStyle: {
                        orientation: this.isMobileDevice ? 'v' : 'h'
                    }
                }
                const layout = {
                    height: this.isSmallContainerWidth ? 250 : 500,
                    title: {
                        text: defs.descr + (this.isSmallContainerWidth ? '<br>' : ' ') + '(' + defs.uom + ') [' + defs.source + ']',
                        font: {
                            size: this.isSmallContainerWidth ? 11 : 14
                        }
                    },
                    autosize: true,
                    margin: {
                        l: 35,
                        r: 25,
                        b: 45,
                        t: 45,
                        pad: 2
                    },
                    xaxis: {
                        type: 'category',
                        showspikes: true,
                        spikemode: 'toaxis+across+marker',
                        spikesnap: 'cursor',
                        spikedash: 'dot',
                        spikethickness: -2, //hack to remove halo
                        spikecolor: 'rgba(40,110,159,.5)'
                    },
                    yaxis: {
                        showspikes: true,
                        spikemode: 'toaxis+across+marker',
                        spikesnap: 'cursor',
                        spikedash: 'dot',
                        spikethickness: -2, //hack to remove halo
                        spikecolor: 'rgba(40,110,159,.5)'
                    },
                    dragmode: false,
                    hovermode: 'x unified',
                    hoverlabel: {bgcolor: "#EEEEEE"},
                    paper_bgcolor: "rgba(0,0,0,0)",
                    plot_bgcolor: "rgba(0,0,0,0)",
                    textposition: 'outside',
                    showlegend: false
                }
                const chartContainer = $('.chart-container', target).empty();
                setTimeout(() => Plotly.newPlot(chartContainer[0], chartData, layout, config), 500);
                this.loader.hide();
            };

            const index = this.indicesDropdown.getValue();
            let data = [];
            if (stats[index])
                data.push({
                    name: countryName,
                    x: stats[index].map(v => v.year),
                    y: stats[index].map(v => v.val),
                    type: 'bar',
                    marker: {
                        color: this.MAIN_COUNTRY_COLOR
                    }
                });
            if (countries.length > 1) {
                const additionalCountries = countries.slice(1);
                this.loader.show();
                $.getJSON(this.countryStatsUrl.format([additionalCountries.map(c => c.code).join(',')]), (fCol) => {
                    fCol.features.forEach(feature => {
                        const country = additionalCountries.filter(c => c.code === feature.properties.cty_id)[0];
                        const countryStats = feature.properties[index];
                        if (countryStats)
                            data.push({
                                name: country.name,
                                x: countryStats.map(v => v.year),
                                y: countryStats.map(v => v.val),
                                type: 'scatter',
                                mode: 'lines+markers',
                                marker: {
                                    color: country.color
                                },
                                connectgaps: true
                            });
                    });
                    renderChart(data, statsDefs[index]);
                });
            } else renderChart(data, statsDefs[index]);
        };

        const dropdownOptions = Object.keys(statsDefs)
            .sort((k1, k2) => statsDefs[k1].descr.localeCompare(statsDefs[k2].descr))
            .map(k => new DropdownOption(this.translator, this.isMobileDevice, '#' + statsDefs[k].descr, k));
        this.indicesDropdown = new Dropdown(this.translator, this.isMobileDevice, 'location_selector_country_comparison_indices', dropdownOptions, this.DEFAULT_INDEX, true, undefined, updateComparisonChart);
        const neighborValues = Object.keys(neighbors)
            .sort((k1, k2) => neighbors[k1].localeCompare(neighbors[k2]));
        const neighborLabels = neighborValues.map(countryCode => neighbors[countryCode]);
        const otherCountryValues = Object.keys(otherCountries)
            .sort((k1, k2) => otherCountries[k1].localeCompare(otherCountries[k2]));
        const otherCountryLabels = otherCountryValues.map(countryCode => otherCountries[countryCode]);
        this.neighborsCheckboxesSet = new CheckboxesSet(this.translator, this.isMobileDevice, 'location_selector_neighbors', neighborValues, neighborLabels, undefined, false, updateComparisonChart);
        this.otherCountriesCheckboxesSet = new CheckboxesSet(this.translator, this.isMobileDevice, 'location_selector_other_countries', otherCountryValues, otherCountryLabels, undefined, false, updateComparisonChart);
        const neighborMultioptionList = new MultiOptionList(this.translator, this.isMobileDevice, this.neighborsCheckboxesSet.getCode() + '_option_list', this.translator.translate('tool.charts.location.comparison.countries.neighbor'), this.neighborsCheckboxesSet);
        const otherCountriesMultioptionList = new MultiOptionList(this.translator, this.isMobileDevice, this.otherCountriesCheckboxesSet.getCode() + '_option_list', this.translator.translate('tool.charts.location.comparison.countries.other'), this.otherCountriesCheckboxesSet);

        const countriesContainer = $('<div class="countries-container"></div>')
            .append($('<div class="neighbors-container"></div>').append(neighborMultioptionList.getUIElement()))
            .append($('<div class="other-countries-container"></div>').append(otherCountriesMultioptionList.getUIElement()));
        target.append($('<div class="indices-container"></div>').append(this.indicesDropdown.getUIElement()))
            .append($('<div class="selected-country-container"></div>'))
            .append(countriesContainer)
            .append($('<div class="chart-container"></div>'));

        updateComparisonChart();
    }

    /**
     * Get the current coordinates for the report
     * @return {Array<number>} 2 position array with coords lon lat in wgs84
     */
    getCoordinatesWgs84() {
        return this.coords;
    }

    /**
     * Get the current date for the report
     * @return {Date}
     */
    getDate() {
        return this.refDate;
    }

    _executeOnChange() {
        this.onChangeCallbacks.forEach(c => c(this));
    }

    _updateDateAndCoords(date, coords) {
        this.loader.show();
        if (coords)
            this.coords = reducePrecisionWgs84(coords);
        if (date)
            this.refDate = date;
        const formattedLon = this.coords[0] + '';
        const formattedLat = this.coords[1] + '';
        $('.coordinates.lon input', this.getContainerElement()).val(formattedLon);
        $('.coordinates.lat input', this.getContainerElement()).val(formattedLat);
        $('.body .desc', this.getContainerElement()).empty();
        $('.body .map-img', this.getContainerElement()).css('background-image', 'none');
        $('.body .stats', this.getContainerElement()).empty();
        $.getJSON(this.townAroundPointUrl.format([formattedLon, formattedLat]), (fCol) => {
            this.closestCityDistance = getDistance(this.coords, fCol.features[0].geometry.coordinates);
            if (Math.abs(fCol.features[0].properties.dist - this.closestCityDistance) > 5) {
                this.logDebug("distance from server: {0}, calculated distance: {1}".format(["" + fCol.features[0].properties.dist, this.closestCityDistance + ""]));
                this.closestCityDistance = fCol.features[0].properties.dist;
            }
            const formattedDistance = formatNumber(this.closestCityDistance / 1000, 2);
            this.closestCityName = fCol.features[0].properties.name;
            $('.body .desc', this.getContainerElement()).html(this.translator.translate('tool.charts.location.desc',
                [formattedDistance, this.closestCityName, formatNumber(fCol.features[0].properties.population)]));
        });
        $.getJSON(this.basicStatsUrl.format([formattedLon, formattedLat]), (fCol) => {
            this.aruId = fCol.features[0].properties.id;
            this.countryCode = fCol.features[0].properties.ctyId;
            this.regionName = fCol.features[0].properties.name;
            this.countryName = fCol.alia && fCol.alia.countries ? fCol.alia.countries[this.countryCode].name : undefined;
            this.isRegion = this.regionName !== this.countryName;

            $('.header .title h3', this.getContainerElement()).html(this.translator.translate('tool.charts.location.name', [
                this.isRegion ? (this.regionName + ", " + nnTrim(this.countryName, this.countryCode)) : nnTrim(this.countryName, this.countryCode),
                this.timeCalculator.timeCodeToString(formatDate(this.refDate))
            ]));

            this.capital = fCol.alia && fCol.alia.countries ? fCol.alia.countries[this.countryCode].capital : undefined;
            this.regionArea = fCol.features[0].properties.area;
            this.regionBbox = fCol.bbox;
            this.countryArea = fCol.alia && fCol.alia.countries ? fCol.alia.countries[this.countryCode].area : undefined;
            this.regionPop = fCol.features[0].properties.population;
            this.regionPopDensity = this.regionPop / (this.regionArea / 1000000);
            const countryPop = fCol.alia && fCol.alia.countries ? fCol.alia.countries[this.countryCode].wb['SP.POP.TOTL'].value : undefined;
            $.getJSON(this.mapImagesUrl.format([formatDate(this.refDate), formattedLon, formattedLat, this.aruId, this.countryCode + ""]), (urls) => {
                this.mapUrls = urls;
                this._executeOnChange();
                const imgUrl = prefixUrl(this.mapUrls.ref);
                $('.body .map-img', this.getContainerElement()).css('background-image', 'url(' + imgUrl + ')');
                this.loader.hide();
            });
            $('.body .stats', this.getContainerElement()).append($(
                '<table class="values"><tbody>' +
                '   <tr><td class="title" colspan="2">' + this.translator.translate('tool.charts.location.stats.title', [nnTrim(this.countryName, this.countryCode)]) + '</td></tr>' +
                '   <tr><td><label>' + this.translator.translate('tool.charts.location.stats.area') + '</label></td><td class="value">' + formatNumber(this.countryArea / 1000000, 2, 'km<sup>2</sup>') + '</td></tr>' +
                '   <tr><td><label>' + this.translator.translate('tool.charts.location.stats.capital') + '</label></td><td class="value">' + nnTrim(this.capital, '--') + '</td></tr>' +
                '   <tr><td class="title" colspan="2">' + this.translator.translate('tool.charts.location.stats.worldBank.title', [nnTrim(this.countryName, this.countryCode)]) + '</td></tr>' +
                '</tbody></table>'
            ));

            this.WB_STAT_CODES.forEach(code => {
                const fixedCode = code.replaceAll('.', '_');
                return $('.body .stats table.values', this.getContainerElement()).append(
                    $('<tr><td><label>' + this.translator.translate('tool.charts.location.stats.worldBank.' + fixedCode) + '</label></td><td class="value ' + fixedCode + '"></td></tr>')
                );
            });

            this.wbDataSeries = {};
            this.WB_STAT_CODES.forEach(indCode => {
                const currentYear = new Date().getFullYear();
                $.getJSON(URL_WORLDBANK_STATS_API.format([this.countryCode, indCode, "" + (currentYear - 20), "" + (currentYear - 1)]), (wbData) => {
                    this.wbDataSeries[indCode] = wbData;
                    const yearValue = wbData[1] ? wbData[1].filter(v => v.value !== null)[0] : undefined;
                    const fixedCode = indCode.replaceAll('.', '_');
                    const valueTarget = $('.body .stats table.values tr td.' + fixedCode, this.getContainerElement());
                    if (yearValue) {
                        valueTarget.html(
                            formatNumber(yearValue.value, yearValue.decimal) + ' <span class="context">(' + yearValue.date + ')</span>'
                        );
                    } else valueTarget.html('--');
                });
            });

            if (this.isRegion) {
                const percPop = this.regionPop / countryPop * 100;
                const percArea = this.regionArea / this.countryArea * 100;
                $('.body .stats table.values tbody', this.getContainerElement()).append($(
                    '   <tr><td class="title" colspan="2">' + this.translator.translate('tool.charts.location.stats.region.title', [this.regionName]) + '</td></tr>' +
                    '   <tr><td><label>' + this.translator.translate('tool.charts.location.stats.region.area') + '</label></td><td class="value">' + formatNumber(this.regionArea / 1000000, 2, 'km<sup>2</sup>') + ' <span class="context">(' + formatNumber(percArea, 2, '%') + ')</span></td></tr>' +
                    '   <tr><td><label>' + this.translator.translate('tool.charts.location.stats.region.population') + '</label></td><td class="value">' + formatNumber(this.regionPop) + ' <span class="context">(' + formatNumber(percPop, 2, '%') + ')</span></td></tr>' +
                    '   <tr><td><label>' + this.translator.translate('tool.charts.location.stats.region.popDensity') + '</label></td><td class="value">' + formatNumber(this.regionPopDensity, 2) + '</td></tr>'
                ));
            }
            $.getJSON(this.countryStatsUrl.format([this.countryCode]), (fCol) => {
                const countryCode = fCol.alia && fCol.alia.countries ? Object.keys(fCol.alia.countries.selected)[0] : undefined;
                this.countryComparisonValues = {
                    countryCode: countryCode,
                    countryName: fCol.alia && fCol.alia.countries ? fCol.alia.countries.selected[countryCode] : undefined,
                    neighbors: fCol.alia && fCol.alia.countries ? fCol.alia.countries.neighbors : undefined,
                    otherCountries: fCol.alia && fCol.alia.countries ? fCol.alia.countries.others : undefined,
                    stats: fCol.features[0].properties,
                    statsDefs: fCol.alia ? fCol.alia.statsDefs : undefined
                };
                if (this.countryComparisonLoaded) {
                    this._generateComparisonChart(
                        this.countryComparisonValues.countryCode,
                        this.countryComparisonValues.countryName,
                        this.countryComparisonValues.neighbors,
                        this.countryComparisonValues.otherCountries,
                        this.countryComparisonValues.stats,
                        this.countryComparisonValues.statsDefs
                    );
                }
            });
        });
    }

    /**
     * Get the URLS for map indicator images
     * @return {Object} dictionary of indicator codes (server custom codes) to img urls
     */
    getMapUrls() {
        return this.mapUrls;
    }

    /**
     * Get the country code
     * @return {string}
     */
    getCountryCode() {
        return this.countryCode;
    }

    /**
     * Get the region ID
     * @return {string}
     */
    getAruId() {
        return this.aruId;
    }

    /**
     * Get the region area
     * @return {number}
     */
    getRegionArea() {
        return this.regionArea;
    }

    /**
     * Get the region bbox
     * @return {Array<number>} 4 position array wuth bbox lon lat in wgs84
     */
    getRegionExtent() {
        return this.regionBbox;
    }

    _startCoordsMap() {
        const coordsMapTarget = $('<div id="coords_map_target" class="coords-map"></div>');

        this.coordsMapModal.setTitle(this.translator.translate('tool.charts.location.chooseFromMap'));
        this.coordsMapModal.setContent(coordsMapTarget);
        this.coordsMapModal.show();

        const source = new VectorSource({wrapX: false});

        const buttons = $(
            '<div class="ol-control buttons ol-unselectable">' +
            '   <button class="button cancel-button">' + this.translator.translate("tool.charts.location.map.cancel") + '</button>' +
            '   <button class="button ok action">' + this.translator.translate("tool.charts.location.map.accept") + '</button> ' +
            '</div>'
        ).hide();
        $('.ok', buttons).click(() => {
            this._updateDateAndCoords(this.refDate, transform(source.getFeatures()[0].getGeometry().getCoordinates(), MERCATOR_CODE, WGS84_CODE));
            this.coordsMapModal.close();
            this.logStat("SET_COORDINATES_IN_MAP");
        });
        $('.cancel-button', buttons).click(() => this.coordsMapModal.close());

        let hasChange = false;
        source.addFeature(new Feature(new Point(transform(this.getCoordinatesWgs84(), WGS84_CODE, MERCATOR_CODE))));
        source.on('change', () => {
            if (!hasChange)
                buttons.show();
            hasChange = true;
        });
        const draw = new Draw({
            source: source,
            type: 'Point',
        });
        draw.on('drawend', () => source.clear()); //we only want 1 feature
        const style = [
            new Style({
                image: new CircleStyle({
                    radius: 8,
                    fill: new Fill({color: 'rgba(231, 165, 37,0.25)'}),
                    stroke: new Stroke({color: '#E7A525', width: 2}),
                }),
            }),
            new Style({
                image: new CircleStyle({
                    radius: 6,
                    stroke: new Stroke({color: '#286e9f', width: 2}),
                }),
            }),
            new Style({
                image: new CircleStyle({
                    radius: 4,
                    stroke: new Stroke({color: '#FFFFFF', width: 2}),
                }),
            })
        ];
        const map = new Map({
            layers: [
                new TileLayer({source: new OSM({url: 'https://gisco-services.ec.europa.eu/maps/tiles/OSMCartoComposite/EPSG3857/{z}/{x}/{y}.png'})}),
                new VectorLayer({source: source, style: style})
            ],
            target: 'coords_map_target',
            view: new View({
                projection: MERCATOR_CODE,
                center: transform(this.getCoordinatesWgs84(), WGS84_CODE, MERCATOR_CODE),
                zoom: 7
            }),
            controls: []
        });
        map.addInteraction(draw);

        $('.ol-overlaycontainer-stopevent', coordsMapTarget).append(buttons);
    }
}

export {SettingsSelector}